import { getTranscriptWindowMs } from '../utils/getTranscriptWindowMs'

const MIME_CANDIDATES = [
	'audio/webm;codecs=opus',
	'audio/webm',
	'audio/mp4',
	'',
]

export function isMediaRecorderSupported() {
	return Boolean(
		typeof window !== 'undefined' &&
			window.MediaRecorder &&
			navigator.mediaDevices?.getUserMedia,
	)
}

export function pickMimeType() {
	if (typeof window === 'undefined' || !window.MediaRecorder?.isTypeSupported) {
		return ''
	}

	for (const type of MIME_CANDIDATES) {
		if (!type || window.MediaRecorder.isTypeSupported(type)) {
			return type
		}
	}

	return ''
}

/**
 * Browser audio capture for the Note Taker.
 *
 * Two recorders share one microphone stream:
 * - The "main" recorder runs continuously and produces a single valid WebM file
 *   on stop (used for storage + playback — never re-encoded).
 * - The "window" recorder is cycled on a variable schedule (5s × 3, then 10s).
 *   Each cycle yields a self-contained WebM file that can be transcribed
 *   independently, so recording length no longer affects STT latency, cost,
 *   or the 25MB Whisper file limit.
 */
export function createNoteTakerMediaRecorder(handlers = {}) {
	let state = 'idle'
	let stream = null
	let mimeType = ''
	let sessionStartMs = 0
	let suppressCallbacks = false

	// Continuous recorder → final saved audio.
	let mainRecorder = null
	let mainChunks = []
	let chunkIndex = 0

	// Cycled recorder → independent STT windows.
	let windowRecorder = null
	let windowChunks = []
	let windowIndex = 0
	let windowTimer = null
	let resolveWindowMs = getTranscriptWindowMs
	let cycling = false

	// Duration limiting
	let durationTimer = null
	let maxDurationMs = 0
	let warningTimeoutMs = null

	const emitError = (code, message, cause) => {
		if (suppressCallbacks) return
		handlers.onError?.({ code, message, cause })
	}

	const assembleMainBlob = () =>
		new Blob(mainChunks, { type: mimeType || 'audio/webm' })

	const clearWindowTimer = () => {
		if (windowTimer) {
			window.clearTimeout(windowTimer)
			windowTimer = null
		}
	}

	const clearDurationTimer = () => {
		if (durationTimer) {
			window.clearTimeout(durationTimer)
			durationTimer = null
		}
		if (warningTimeoutMs) {
			window.clearTimeout(warningTimeoutMs)
			warningTimeoutMs = null
		}
	}

	const scheduleDurationLimit = (limitMs, warnBeforeMs = 0) => {
		clearDurationTimer()

		// Optional warning callback before hitting the limit
		if (warnBeforeMs > 0 && warnBeforeMs < limitMs) {
			warningTimeoutMs = window.setTimeout(() => {
				if (!suppressCallbacks) {
					handlers.onMaxDurationWarning?.({
						remainingMs: limitMs - warnBeforeMs,
						totalMaxMs: limitMs,
						timestampMs: performance.now() - sessionStartMs,
					})
				}
			}, warnBeforeMs)
		}

		// Stop recording when max duration is reached
		durationTimer = window.setTimeout(() => {
			if (state === 'recording' || state === 'paused') {
				try {
					cycling = false
					clearWindowTimer()

					if (!suppressCallbacks) {
						handlers.onMaxDurationReached?.({
							totalMs: performance.now() - sessionStartMs,
							maxMs: limitMs,
						})
					}

					// Stop the recorders
					if (mainRecorder && mainRecorder.state !== 'inactive') {
						mainRecorder.stop()
					}
				} catch (_) {
					// Ignore errors
				}
			}
		}, limitMs)
	}

	const scheduleWindowCycle = durationMs => {
		clearWindowTimer()
		windowTimer = window.setTimeout(() => {
			if (windowRecorder && windowRecorder.state !== 'inactive') {
				try {
					windowRecorder.stop()
				} catch (_) {
					// Recorder may already be inactive.
				}
			}
		}, durationMs)
	}

	const startWindowRecorder = () => {
		if (!stream) return

		const currentWindowMs = resolveWindowMs(windowIndex)

		windowChunks = []
		const options = mimeType ? { mimeType } : undefined
		const rec = new window.MediaRecorder(stream, options)

		rec.addEventListener('dataavailable', event => {
			if (event.data && event.data.size > 0) {
				windowChunks.push(event.data)
			}
		})

		rec.addEventListener('stop', () => {
			const blob = new Blob(windowChunks, { type: mimeType || 'audio/webm' })
			const index = windowIndex
			windowIndex += 1

			if (!suppressCallbacks && blob.size > 0) {
				handlers.onWindow?.({
					blob,
					mimeType,
					windowIndex: index,
					timestampMs: performance.now() - sessionStartMs,
				})
			}

			if (cycling) {
				startWindowRecorder()
			}
		})

		windowRecorder = rec
		rec.start()
		scheduleWindowCycle(currentWindowMs)
	}

	const handleMainDataAvailable = event => {
		if (!event.data || event.data.size === 0) return

		if (!suppressCallbacks) {
			handlers.onChunk?.({
				blob: event.data,
				mimeType,
				chunkIndex,
				timestampMs: performance.now() - sessionStartMs,
			})
		}
		chunkIndex += 1
		mainChunks.push(event.data)
	}

	const stopRecorderAndWait = rec =>
		new Promise(resolve => {
			if (!rec || rec.state === 'inactive') {
				resolve()
				return
			}
			rec.addEventListener('stop', () => resolve(), { once: true })
			try {
				rec.stop()
			} catch (_) {
				resolve()
			}
		})

	const releaseStream = () => {
		if (!stream) return
		stream.getTracks().forEach(track => track.stop())
		stream = null
	}

	const disposeInternal = ({ silent = false } = {}) => {
		if (silent) {
			suppressCallbacks = true
		}

		cycling = false
		clearWindowTimer()
		clearDurationTimer()

		for (const rec of [windowRecorder, mainRecorder]) {
			if (rec && rec.state !== 'inactive') {
				try {
					rec.stop()
				} catch (_) {
					// Recorder may already be inactive.
				}
			}
		}

		windowRecorder = null
		mainRecorder = null
		releaseStream()
		mainChunks = []
		windowChunks = []
		chunkIndex = 0
		windowIndex = 0
		state = 'idle'
		suppressCallbacks = false
	}

	return {
		getState: () => state,
		isSupported: isMediaRecorderSupported,

		async start({ timesliceMs = 1000, getWindowMs = getTranscriptWindowMs, maxDurationMs: max, maxDurationWarningMs = 0 } = {}) {
			if (state !== 'idle') {
				throw new Error('Recorder already active')
			}

			if (!isMediaRecorderSupported()) {
				const error = new Error('MediaRecorder is not supported in this browser')
				error.name = 'NotSupportedError'
				emitError('NotSupportedError', error.message, error)
				throw error
			}

			state = 'starting'
			resolveWindowMs =
				typeof getWindowMs === 'function' ? getWindowMs : getTranscriptWindowMs
			maxDurationMs = typeof max === 'number' && max > 0 ? max : 0

			try {
				stream = await navigator.mediaDevices.getUserMedia({ audio: true })
				mimeType = pickMimeType()
				const options = mimeType ? { mimeType } : undefined

				mainRecorder = new window.MediaRecorder(stream, options)
				mimeType = mainRecorder.mimeType || mimeType
				mainChunks = []
				windowChunks = []
				chunkIndex = 0
				windowIndex = 0
				sessionStartMs = performance.now()

				mainRecorder.addEventListener('dataavailable', handleMainDataAvailable)
				mainRecorder.addEventListener('error', event => {
					const cause = event.error || event
					emitError(
						cause?.name || 'MediaRecorderError',
						cause?.message || 'Recording failed',
						cause,
					)
				})

				mainRecorder.start(timesliceMs)

				cycling = true
				startWindowRecorder()

				// Set up duration limiting if maxDurationMs is specified
				if (maxDurationMs > 0) {
					scheduleDurationLimit(maxDurationMs, maxDurationWarningMs)
				}

				state = 'recording'
			} catch (cause) {
				disposeInternal({ silent: true })
				const code = cause?.name || 'StartFailed'
				emitError(code, cause?.message || 'Failed to start recording', cause)
				throw cause
			}
		},

		async pause() {
			if (state !== 'recording') return

			if (typeof mainRecorder?.pause !== 'function') {
				const error = new Error('Pause is not supported in this browser')
				error.name = 'NotSupportedError'
				throw error
			}

			// Stop the current window so its audio is transcribed promptly; do not
			// auto-restart while paused.
			cycling = false
			clearWindowTimer()
			await stopRecorderAndWait(windowRecorder)
			windowRecorder = null

			mainRecorder.pause()
			state = 'paused'
		},

		async resume() {
			if (state !== 'paused' || !mainRecorder) return

			if (typeof mainRecorder.resume !== 'function') {
				const error = new Error('Resume is not supported in this browser')
				error.name = 'NotSupportedError'
				throw error
			}

			mainRecorder.resume()
			cycling = true
			startWindowRecorder()
			state = 'recording'
		},

		async stop() {
			if (!mainRecorder) {
				return {
					blob: assembleMainBlob(),
					mimeType,
					chunkCount: chunkIndex,
					durationMs: performance.now() - sessionStartMs,
				}
			}

			if (state === 'stopping') return

			state = 'stopping'
			cycling = false
			clearWindowTimer()
			clearDurationTimer()

			// Flush the final (partial) window to STT, then finalize saved audio.
			await stopRecorderAndWait(windowRecorder)
			windowRecorder = null
			await stopRecorderAndWait(mainRecorder)

			const payload = {
				blob: assembleMainBlob(),
				mimeType,
				chunkCount: chunkIndex,
				durationMs: performance.now() - sessionStartMs,
			}

			if (!suppressCallbacks) {
				handlers.onStop?.(payload)
			}

			mainRecorder = null
			state = 'idle'
			return payload
		},

		dispose({ suppressCallbacks: silent = false } = {}) {
			disposeInternal({ silent })
		},
	}
}
